import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api, { inr } from "../lib/api";
import { IMG, ASSETS, CAMPAIGN, optImg } from "../lib/assets";
import { MaskedLine, Reveal, Marquee } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import NewArrivals from "../components/NewArrivals";

const CATEGORY_TILES = [
  { key: "men", name: "Men", img: IMG.hero2, link: "/shop?gender=men" },
  { key: "women", name: "Women", img: IMG.womenCampaign, link: "/shop?gender=women" },
  { key: "tshirts", name: "T-Shirts", img: IMG.tshirts, link: "/shop?category=tshirts" },
  { key: "baggy-jeans", name: "Baggy Jeans", img: IMG.jeans, link: "/shop?category=baggy-jeans" },
  { key: "sweatshirts", name: "Sweatshirts", img: IMG.sweatshirts, link: "/shop?category=sweatshirts" },
  { key: "trousers", name: "Trousers", img: IMG.trousers, link: "/shop?category=trousers" },
];

const COLOURS = [
  { name: "Off White", hex: "#FDFBF7", border: true },
  { name: "Washed Black", hex: "#1C1B1A" },
  { name: "Forest Green", hex: "#183A2E" },
  { name: "Espresso Brown", hex: "#4A3428" },
  { name: "Midnight Navy", hex: "#1E2A3A" },
  { name: "Stone Grey", hex: "#A7A39B" },
  { name: "Burgundy", hex: "#6A1E2C" },
];

// Local fallback mirrors the backend DEFAULT_HOME_CONTENT so the homepage never breaks if the API is unreachable
const DEFAULT_SECTIONS = [
  { id: "hero", type: "hero", enabled: true, fields: {
    overline: "Estd. 2024 — Designed in India", heading1: "DEFINED", heading2: "BY DESIGN.",
    subtext: "Contemporary essentials. Elevated everyday.",
    cta1_label: "Shop New Arrivals", cta1_link: "/shop?sort=newest",
    cta2_label: "Explore the Collection", cta2_link: "/shop",
    image: CAMPAIGN.trio, image_mobile: "" } },
  { id: "categories", type: "categories", enabled: true, fields: { overline: "The Wardrobe", title: "SHOP BY CATEGORY" } },
  { id: "new_arrivals", type: "new_arrivals", enabled: true, fields: { overline: "Just Landed", title: "NEW ARRIVALS" } },
  { id: "featured", type: "featured", enabled: true, fields: { overline: "Featured Collection", title: "THE NEW STANDARD" } },
  { id: "campaign", type: "campaign", enabled: true, fields: {
    image: "", overline: "The Signature Approach", heading1: "LESS,", heading2: "BUT BETTER.",
    text: "Designed for everyday movement. Made to define your own style. Oversized silhouettes, heavyweight 240 GSM cotton, washed denim, and branding that whispers.",
    cta_label: "Discover Our Approach", cta_link: "/story" } },
  { id: "craftsmanship", type: "craftsmanship", enabled: true, fields: {
    image: CAMPAIGN.ivoryTee, overline: "Craftsmanship", heading1: "MADE TO", heading2: "BE FELT.",
    text: "Heavyweight 240 GSM combed cotton, bio-washed for a lived-in hand feel. Ribbed collars that keep their shape, stitching built to outlast trends.",
    cta_label: "Shop T-Shirts", cta_link: "/shop?category=tshirts" } },
  { id: "colour_story", type: "colour_story", enabled: true, fields: { overline: "The Palette", title: "SHOP THE COLOUR STORY" } },
  { id: "denim", type: "denim", enabled: true, fields: {
    image: "", overline: "The Denim Edit", heading1: "THE ART", heading2: "OF DENIM.",
    text: "Rigid 13oz denim, column-wide legs, stacked hems. Washes that fade with you.",
    cta_label: "Explore Denim", cta_link: "/shop?category=baggy-jeans" } },
  { id: "brand_story", type: "brand_story", enabled: true, fields: {
    image: "", overline: "The House", heading1: "BUILT ON", heading2: "SIMPLICITY.",
    text: "VELOUR & VANE brings together considered design, contemporary silhouettes, and everyday versatility. We create modern wardrobe essentials with an emphasis on fit, texture, detail, and understated expression.",
    cta_label: "Discover Velour & Vane", cta_link: "/story" } },
  { id: "journal", type: "journal", enabled: true, fields: {
    overline: "A Study in Style", title: "THE VELOUR & VANE JOURNAL", image1: "", image2: "" } },
  { id: "newsletter", type: "newsletter", enabled: true, fields: {
    overline: "Letters from Velour & Vane", heading1: "NEW COLLECTIONS,", heading2: "FIRST ACCESS.",
    text: "Considered edits and first access to selected releases. No noise — one letter, occasionally." } },
];

function Hero({ f }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[92vh] min-h-[560px] overflow-hidden bg-vv-black">
      <motion.div style={{ y }} className="absolute inset-0">
        <motion.picture initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}>
          {f.image_mobile ? <source media="(max-width: 767px)" srcSet={optImg(f.image_mobile, 1080)} /> : null}
          <img src={optImg(f.image || IMG.hero, 1920)} alt="Velour & Vane campaign" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
        </motion.picture>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-vv-black/70 via-vv-black/20 to-vv-black/30" />
      <motion.div style={{ opacity }} className="relative h-full flex flex-col justify-end px-4 sm:px-8 lg:px-16 max-w-[1600px] mx-auto pb-20 md:pb-28">
        <MaskedLine delay={0.5} className="mb-4">
          <span className="vv-overline text-vv-champagne">{f.overline}</span>
        </MaskedLine>
        <h1 className="font-display text-vv-ivory leading-[0.95] tracking-tight text-[15vw] sm:text-[11vw] lg:text-[8.5vw]">
          <MaskedLine delay={0.7}><span>{f.heading1}</span></MaskedLine>
          <MaskedLine delay={0.85}><span className="italic font-light">{f.heading2}</span></MaskedLine>
        </h1>
        <MaskedLine delay={1.05} className="mt-6">
          <p className="text-vv-ivory/80 text-sm md:text-base max-w-md font-light tracking-wide">{f.subtext}</p>
        </MaskedLine>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8 }}
          className="flex flex-wrap gap-4 mt-10">
          {f.cta1_label && (
            <Link data-testid="hero-shop-new-button" to={f.cta1_link || "/shop"} className="vv-btn bg-vv-ivory text-vv-black hover:bg-vv-champagne">
              {f.cta1_label}
            </Link>
          )}
          {f.cta2_label && (
            <Link data-testid="hero-explore-button" to={f.cta2_link || "/shop"} className="vv-btn-outline">
              {f.cta2_label}
            </Link>
          )}
        </motion.div>
      </motion.div>
    </section>
  );
}

function SectionHead({ overline, title, link, linkLabel }) {
  return (
    <Reveal className="flex items-end justify-between gap-6 mb-12 md:mb-16">
      <div>
        <p className="vv-overline text-vv-muted mb-4">{overline}</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">{title}</h2>
      </div>
      {link && (
        <Link data-testid={`section-link-${linkLabel?.toLowerCase().replace(/\s/g, "-")}`} to={link}
          className="hidden sm:inline-flex items-center gap-2 vv-link-underline text-[11px] uppercase tracking-[0.25em] shrink-0">
          {linkLabel} <ArrowRight size={14} strokeWidth={1.5} />
        </Link>
      )}
    </Reveal>
  );
}

function CategorySection({ f }) {
  return (
    <section data-testid="category-section" className="px-4 sm:px-8 lg:px-16 max-w-[1600px] mx-auto py-16 md:py-28">
      <Reveal className="flex items-end justify-between gap-6 mb-10 md:mb-14">
        <div>
          <p className="vv-overline text-vv-muted mb-4">{f.overline}</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">{f.title}</h2>
        </div>
        <Link data-testid="section-link-view-all" to="/shop"
          className="hidden sm:inline-flex items-center gap-2 vv-link-underline text-[11px] uppercase tracking-[0.25em] shrink-0">
          View All <ArrowRight size={14} strokeWidth={1.5} />
        </Link>
      </Reveal>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6 lg:gap-8">
        {CATEGORY_TILES.map((c, i) => (
          <Reveal key={c.key} delay={(i % 3) * 0.07} y={24}>
            <Link data-testid={`category-card-${c.key}`} to={c.link}
              className="group block relative overflow-hidden bg-vv-elevated aspect-[3/4] md:aspect-[4/5]">
              <img src={optImg(c.img, 800)} alt={`${c.name} collection`} loading={i < 3 ? "eager" : "lazy"} decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]" />
              <div className="absolute inset-0 bg-gradient-to-t from-vv-black/55 via-transparent to-transparent transition-opacity duration-700 group-hover:from-vv-black/65" />
              <div className="absolute bottom-0 inset-x-0 p-4 md:p-6">
                <p className="font-display text-lg md:text-2xl text-vv-ivory leading-none tracking-tight">{c.name}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function CampaignSection({ f, testid, dark = true, imageFirst = true }) {
  return (
    <section data-testid={testid} className="grid md:grid-cols-2 min-h-[80vh]">
      <div className={`relative overflow-hidden ${imageFirst ? "order-2 md:order-1" : "order-2"}`}>
        <Reveal className="h-full" y={0}>
          <img src={optImg(f.image || ASSETS.boardIvory, 1200)} alt={f.overline} loading="lazy"
            className="w-full h-full object-cover min-h-[420px]" />
        </Reveal>
      </div>
      <div className={`${dark ? "bg-vv-black text-vv-ivory" : "bg-vv-elevated text-vv-black"} flex items-center ${imageFirst ? "order-1 md:order-2" : "order-1"} px-6 sm:px-12 lg:px-20 py-20 md:py-0`}>
        <div>
          <Reveal><p className={`vv-overline mb-6 ${dark ? "text-vv-champagne" : "text-vv-muted"}`}>{f.overline}</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              {f.heading1}<br /><span className="italic font-light">{f.heading2}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className={`${dark ? "text-vv-ivory/60" : "text-vv-muted"} mt-8 max-w-md leading-relaxed font-light`}>{f.text}</p>
          </Reveal>
          {f.cta_label && (
            <Reveal delay={0.3}>
              <Link data-testid={`${testid}-cta`} to={f.cta_link || "/shop"} className={`${dark ? "vv-btn-outline" : "vv-btn-outline-dark"} mt-10`}>{f.cta_label}</Link>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [sections, setSections] = useState(DEFAULT_SECTIONS);

  useEffect(() => {
    api.get("/products?featured=true").then((r) => setFeatured(r.data.slice(0, 10))).catch(() => {});
    api.get("/content/homepage").then((r) => {
      const remote = r.data?.sections;
      if (!Array.isArray(remote) || !remote.length) return;
      const merged = remote.map((s) => {
        const def = DEFAULT_SECTIONS.find((d) => d.id === s.id);
        return { ...def, ...s, fields: { ...(def?.fields || {}), ...(s.fields || {}) } };
      });
      // surface any new local section types the API doesn't know about yet
      DEFAULT_SECTIONS.forEach((d) => { if (!merged.find((m) => m.id === d.id)) merged.push(d); });
      setSections(merged);
    }).catch(() => {});
  }, []);

  const renderSection = (s) => {
    const f = s.fields;
    switch (s.type) {
      case "hero": return <Hero f={f} />;
      case "categories": return <><CategorySection f={f} /><Marquee items={["Timeless", "Refined", "Yours", "Collection 01", "240 GSM Heavyweight", "Beyond the Ordinary"]} /></>;
      case "new_arrivals": return <NewArrivals />;
      case "featured": return (
        <section data-testid="featured-section" className="bg-vv-elevated/60 py-16 md:py-24">
          <div className="px-4 sm:px-8 lg:px-16 max-w-[1600px] mx-auto">
            <SectionHead overline={f.overline} title={f.title} link="/shop" linkLabel="View All" />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-3 gap-y-8 md:gap-x-5 md:gap-y-10">
              {featured.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.08}>
                  <ProductCard product={p} index={i} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      );
      case "campaign": return <CampaignSection f={f} testid="signature-campaign" dark imageFirst />;
      case "craftsmanship": return <CampaignSection f={{ ...f, image: f.image || CAMPAIGN.ivoryTee }} testid="craftsmanship-section" dark={false} imageFirst={false} />;
      case "colour_story": return (
        <section data-testid="colour-story" className="px-4 sm:px-8 lg:px-16 max-w-[1600px] mx-auto py-20 md:py-32">
          <SectionHead overline={f.overline} title={f.title} />
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-6">
            {COLOURS.map((c, i) => (
              <Reveal key={c.name} delay={i * 0.05}>
                <Link data-testid={`colour-${c.name.toLowerCase().replace(/\s/g, "-")}`} to={`/shop?color=${encodeURIComponent(c.name)}`}
                  className="group block text-center">
                  <div className={`aspect-square rounded-full overflow-hidden border transition-transform duration-500 group-hover:scale-95 ${c.border ? "border-vv-black/20" : "border-transparent"}`}
                    style={{ background: c.hex }} />
                  <p className="text-[10px] uppercase tracking-[0.2em] mt-4 text-vv-muted group-hover:text-vv-black transition-colors">{c.name}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      );
      case "denim": return (
        <section data-testid="denim-edit" className="relative h-[80vh] min-h-[520px] overflow-hidden">
          <img src={optImg(f.image || IMG.denimArt, 1600)} alt={f.overline} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-vv-black/45" />
          <div className="relative h-full flex flex-col justify-center items-start px-4 sm:px-8 lg:px-16 max-w-[1600px] mx-auto">
            <Reveal><p className="vv-overline text-vv-champagne mb-6">{f.overline}</p></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display text-vv-ivory text-5xl sm:text-6xl lg:text-7xl tracking-tight">{f.heading1}<br /><span className="italic font-light">{f.heading2}</span></h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-vv-ivory/70 mt-6 max-w-md font-light leading-relaxed">{f.text}</p>
            </Reveal>
            {f.cta_label && (
              <Reveal delay={0.3}>
                <Link data-testid="denim-explore-button" to={f.cta_link || "/shop"} className="vv-btn-outline mt-10">{f.cta_label}</Link>
              </Reveal>
            )}
          </div>
        </section>
      );
      case "brand_story": return (
        <section data-testid="brand-story" className="px-4 sm:px-8 lg:px-16 max-w-[1600px] mx-auto py-20 md:py-32 grid md:grid-cols-2 gap-12 lg:gap-24 items-center">
          <Reveal>
            <p className="vv-overline text-vv-muted mb-6">{f.overline}</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              {f.heading1}<br /><span className="italic font-light">{f.heading2}</span>
            </h2>
            <p className="text-vv-muted mt-8 leading-relaxed max-w-lg font-light">{f.text}</p>
            {f.cta_label && <Link data-testid="story-discover-button" to={f.cta_link || "/story"} className="vv-btn-outline-dark mt-10">{f.cta_label}</Link>}
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden">
              <img src={optImg(f.image || ASSETS.boardBrand, 1000)} alt="Velour & Vane brand story" loading="lazy"
                className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-[1500ms]" />
            </div>
          </Reveal>
        </section>
      );
      case "journal": return (
        <section id="journal" data-testid="journal-section" className="bg-vv-black text-vv-ivory py-20 md:py-32">
          <div className="px-4 sm:px-8 lg:px-16 max-w-[1600px] mx-auto">
            <Reveal className="mb-12 md:mb-16">
              <p className="vv-overline text-vv-champagne mb-4">{f.overline}</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">{f.title}</h2>
            </Reveal>
            <div className="grid md:grid-cols-8 gap-6 md:gap-10">
              <Reveal className="md:col-span-5">
                <Link to="/shop" data-testid="journal-entry-1" className="group block relative overflow-hidden">
                  <img src={optImg(f.image1 || ASSETS.boardMountain, 1200)} alt="Archive back-print editorial" loading="lazy"
                    className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                  <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-vv-black/70 to-transparent">
                    <p className="vv-overline text-vv-champagne mb-2">Editorial 01</p>
                    <p className="font-display text-2xl">The Archive Print</p>
                  </div>
                </Link>
              </Reveal>
              <Reveal delay={0.1} className="md:col-span-3 md:mt-20">
                <Link to="/shop?category=tshirts" data-testid="journal-entry-2" className="group block relative overflow-hidden">
                  <img src={optImg(f.image2 || ASSETS.boardTees, 800)} alt="Folded heavyweight tees" loading="lazy"
                    className="w-full aspect-[4/5] object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                  <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-vv-black/70 to-transparent">
                    <p className="vv-overline text-vv-champagne mb-2">Editorial 02</p>
                    <p className="font-display text-2xl">Heavyweight, Folded</p>
                  </div>
                </Link>
              </Reveal>
            </div>
          </div>
        </section>
      );
      case "newsletter": return <Newsletter f={f} />;
      default: return null;
    }
  };

  return (
    <main data-testid="home-page">
      {sections.filter((s) => s.enabled !== false).map((s) => {
        const node = renderSection(s);
        return node ? <div key={s.id}>{node}</div> : null;
      })}
    </main>
  );
}

export function Newsletter({ f }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const fields = f || DEFAULT_SECTIONS.find((s) => s.id === "newsletter").fields;

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      setDone(true);
    } catch {
      setDone(false);
    }
  };

  return (
    <section data-testid="newsletter-section" className="px-4 sm:px-8 lg:px-16 max-w-[1600px] mx-auto py-20 md:py-32">
      <div className="border border-vv-champagne px-6 sm:px-12 py-16 md:py-24 text-center max-w-3xl mx-auto">
        <Reveal>
          <p className="vv-overline text-vv-muted mb-4">{fields.overline}</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">{fields.heading1}<br /><span className="italic font-light">{fields.heading2}</span></h2>
          <p className="text-vv-muted text-sm mt-6 max-w-md mx-auto font-light">{fields.text}</p>
          {done ? (
            <p data-testid="newsletter-success" className="mt-10 text-sm text-vv-forest tracking-wide">Welcome to the letters. You are on the list.</p>
          ) : (
            <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input data-testid="newsletter-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="flex-1 bg-transparent border-b border-vv-black/30 focus:border-vv-black outline-none py-3 text-sm placeholder:text-vv-stone" />
              <button data-testid="newsletter-subscribe-button" type="submit" className="vv-btn-dark">Subscribe</button>
            </form>
          )}
          <p className="text-[10px] text-vv-stone mt-6">By subscribing you agree to our privacy policy. Unsubscribe anytime.</p>
        </Reveal>
      </div>
    </section>
  );
}
